import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export const useFilteredSites = () => {
	const pageBuilder = useSelector( ( state ) => state.pageBuilder );
	const filterplan = useSelector( ( state ) => state.filterplan );
	const stEnableTemplates = useSelector( ( state ) => state.stEnableTemplates );

	const allSites = AstraShowcaseVars.sites;
	const sites = [];

	if ( pageBuilder ) {
		for ( const siteId in allSites ) {
			if ( pageBuilder === allSites[ siteId ][ 'astra-site-page-builder' ] ) {
				sites[ siteId ] = allSites[ siteId ];
			}
		}
	}

	if ( filterplan ) {
		for ( const siteId in sites ) {
			if ( 'free' !== sites[ siteId ][ 'astra-sites-type' ] ) {
				sites[ siteId ] = sites[ siteId ];
			} else {
				delete sites[ siteId ];
			}
		}
	}

	if ( stEnableTemplates ) {
		for ( const siteId in sites ) {
			sites[ siteId ][ 'astra-site-live-url' ] = !! sites[ siteId ]['astra-site-live-url']
				? sites[ siteId ][ 'astra-site-live-url' ]
				: sites[ siteId ][ 'astra-site-url' ];

			sites[ siteId ][ 'astra-site-url' ] = updateHost(sites[ siteId ][ 'astra-site-url' ]);
			sites[ siteId ] = allSites[ siteId ];
		}
	}

	for ( const siteId in sites ) {
		if ( sites[ siteId ][ 'astra-site-page-builder' ] === 'gutenberg' ) {
			if ( sites[ siteId ][ 'related-elementor-template' ] !== '' || sites[ siteId ].related_ecommerce_template || Object.values( sites[ siteId ][ 'astra-sites-tag' ] ).includes( 'ecommerce' ) ) {
				sites[ siteId ].available_page_builders = [ 'gutenberg', 'elementor' ];
			} else {
				sites[ siteId ].available_page_builders = [ 'gutenberg' ];
			}
		} else if ( sites[ siteId ][ 'astra-site-page-builder' ] === 'elementor' ) {
			if ( sites[ siteId ][ 'related-gutenberg-template' ] !== '' ) {
				sites[ siteId ].available_page_builders = [ 'gutenberg', 'elementor' ];
			} else {
				sites[ siteId ].available_page_builders = [ 'elementor' ];
			}
		}
	}

	return sites;
};

export const useFilteredCategories = ( categories = [], pageBuilder = '', showWithDesc = false, count = 20 ) => {
	const stEnableTemplates = useSelector( ( state ) => state.stEnableTemplates );
	const sites = AstraShowcaseVars.sites;
	const filteredSites = {};
	const keysToCheck = [
		'astra-sites-tag',
		'astra-sites-template-category',
		'categories',
		'astra-sites-type',
	];

	// If no category is provided, return the first 'count' sites
	if ( ! categories || ( Array.isArray( categories ) && categories.length === 0 && categories[ 0 ] === '' ) ) {
		Object.entries( sites )
			.slice( 0, count )
			.forEach( ( [ siteId, site ] ) => ( filteredSites[ siteId ] = site ) );
	} else {
		const categoryList = Array.isArray( categories )
			? categories.map( ( cat ) => cat.toLowerCase() ) // Convert array to lowercase
			: [ categories.toLowerCase() ]; // Convert string to array for consistency

		Object.entries( sites ).some( ( [ siteId, site ] ) => {
			if (
				keysToCheck.some( ( key ) => {
					const siteCategories = site[ key ];

					if ( ! siteCategories ) {
						return false;
					}

					// Check if siteCategories matches any category in the categoryList
					if ( typeof siteCategories === 'string' ) {
						return categoryList.includes( siteCategories.toLowerCase() );
					}
					if ( Array.isArray( siteCategories ) ) {
						return siteCategories.some( ( val ) => categoryList.includes( val.toLowerCase() ) );
					}
					if ( typeof siteCategories === 'object' ) {
						return Object.values( siteCategories ).some(
							( val ) => typeof val === 'string' && categoryList.includes( val.toLowerCase() )
						);
					}

					return false;
				} )
			) {
				if ( site[ 'astra-site-page-builder' ] === 'gutenberg' ) {
					if ( site[ 'related-elementor-template' ] !== '' || site.related_ecommerce_template || Object.values( site[ 'astra-sites-tag' ] ).includes( 'ecommerce' ) ) {
						site.available_page_builders = [ 'gutenberg', 'elementor' ];
					} else {
						site.available_page_builders = [ 'gutenberg' ];
					}
				} else if ( site[ 'astra-site-page-builder' ] === 'elementor' ) {
					if ( site[ 'related-gutenberg-template' ] !== '' ) {
						site.available_page_builders = [ 'gutenberg', 'elementor' ];
					} else {
						site.available_page_builders = [ 'elementor' ];
					}
				}
				filteredSites[ siteId ] = site;
			}
			return false;
		} );
	}

	if ( pageBuilder !== '' ) {
		for ( const siteId in filteredSites ) {
			if ( pageBuilder !== filteredSites[ siteId ][ 'astra-site-page-builder' ] ) {
				delete filteredSites[ siteId ];
			}
		}
	}

	// Update URLs if `stEnableTemplates` is enabled
	if ( stEnableTemplates ) {
		Object.values( filteredSites ).forEach( ( site ) => {
			site[ 'astra-site-live-url' ] = site[ 'astra-site-live-url' ] || site[ 'astra-site-url' ];
			site[ 'astra-site-url' ] = updateHost( site[ 'astra-site-url' ] );
		} );
	}

	if ( showWithDesc ) {
		Object.entries( filteredSites ).forEach( ( [ siteId, site ] ) => {
			if ( site[ 'post-excerpt' ] === '' ) {
				delete filteredSites[ siteId ];
			}
		} );
	}

	return Object.fromEntries( Object.entries( filteredSites ).slice( 0, count ) );
};

export const updateHost = ( url ) => {
	if ( url.indexOf( AstraShowcaseVars.siteUrl + '/templates/' ) === 0 ) {
		return url;
	}
	const parser = document.createElement( 'a' );
	parser.href = url;
	let path;
	if ( parser.pathname ) {
		path = parser.pathname.substring( 1 );
	}

	if (
		getUrlParameter( 'page-builder' ) !== 'elementor' &&
		getUrlParameter( 'page-builder' ) !== '' &&
		getUrlParameter( 'page-builder' ) !== null
	) {
		return '#';
	}

	return AstraShowcaseVars.siteUrl + '/templates/' + path + '/';
};

const getUrlParameter = ( name ) => {
	const urlParams = new URLSearchParams( window.location.search );
	return urlParams.get( name );
};

export const removeEncoding = ( str ) => {
	return str
		.replace( /&amp;/g, '&' )
		.replace( /&lt;/g, '<' )
		.replace( /&gt;/g, '>' )
		.replace( /&#038;/g, '&' );
};

export const useSitesBySiteType = ( sitesData ) => {
	const { filterplan } = useSelector( ( state ) => state );
	const filteredSites = [];

	if ( filterplan !== 'agency-mini' ) {
		return sitesData;
	}

	for ( const siteId in sitesData ) {
		if ( sitesData[ siteId ][ 'astra-sites-type' ] !== 'free' ) {
			filteredSites[ siteId ] = sitesData[ siteId ];
		}
	}

	return filteredSites;
};

export const useSitesBySearchTerm = ( defaultData ) => {
	const allSites = useFilteredSites();
	const {
		filtersortby,
		pageBuilder,
		filterplan,
		showSearchBox,
		searchTerm: dynamicSearchTerm,
	} = useSelector( ( state ) => state );
	const [ sitesData, setSitesData ] = useState( [] );
	const filteredSites = [];

	const searchResult = async () => {
		const searchTerm =
			document
				.getElementById( 'astra-sites-grid' )
				.getAttribute( 'data-search-term' ) ?? '';
		const relatedTemplates =
			document
				.getElementById( 'astra-sites-grid' )
				.getAttribute( 'data-related-templates' ) ?? '';

		if ( !! searchTerm ) {
			try {
				const response = await fetch(
					`${ AstraShowcaseVars.api_domain }wp-json/starter-templates/v2/sites-search/?search=${ searchTerm }&page-builder=${ pageBuilder }&type=${ filterplan }`
				);
				const jsonData = await response.json();
				const siteIds = jsonData.ids;

				for ( const siteId of siteIds ) {
					if ( !! allSites[ siteId ] ) {
						filteredSites[ siteId ] = allSites[ siteId ];
					}
				}

				setSitesData( filteredSites );
			} catch ( error ) {
				setSitesData( allSites );
			}
		} else {
			setSitesData( allSites );
		}

		if ( !! relatedTemplates ) {
			const siteIds = relatedTemplates.split( ',' );
			for ( const siteId of siteIds ) {
				if ( !! allSites[ siteId ] ) {
					filteredSites[ siteId ] = allSites[ siteId ];
				}
			}

			setSitesData( filteredSites );
		}
	};

	useEffect( () => {
		searchResult();
	}, [ filtersortby, pageBuilder, filterplan ] );

	if ( showSearchBox && dynamicSearchTerm ) {
		return defaultData;
	}

	return sitesData;
};

export const range = ( start, end ) => {
	const length = end - start + 1;

	return Array.from( { length }, ( _, idx ) => idx + start );
};

export const usePagination = (
	totalCount,
	pageSize,
	siblingCount = 1,
	currentPage
) => {
	const DOTS = '...';
	const paginationRange = useMemo( () => {
		const totalPageCount = Math.ceil( totalCount / pageSize );

		const totalPageNumbers = siblingCount + 5;

		if ( totalPageNumbers >= totalPageCount ) {
			return range( 1, totalPageCount );
		}

		const leftSiblingIndex = Math.max( currentPage - siblingCount, 1 );
		const rightSiblingIndex = Math.min(
			currentPage + siblingCount,
			totalPageCount
		);

		const shouldShowLeftDots = leftSiblingIndex > 2;
		const shouldShowRightDots = rightSiblingIndex <= totalPageCount;

		const firstPageIndex = 1;

		if ( ! shouldShowLeftDots && shouldShowRightDots ) {
			const leftItemCount = 1 + 2 * siblingCount;
			const leftRange = range( 1, leftItemCount );

			return [ ...leftRange, DOTS ];
		}

		if ( shouldShowLeftDots && ! shouldShowRightDots ) {
			return [ firstPageIndex, DOTS ];
		}

		if ( shouldShowLeftDots && shouldShowRightDots ) {
			const middleRange = range( leftSiblingIndex, rightSiblingIndex );
			return [ firstPageIndex, ...middleRange, DOTS ];
		}
	}, [ totalCount, pageSize, siblingCount, currentPage ] );

	return paginationRange;
};

export const getQueryString = ( url ) => {
	const questionMarkIndex = url.indexOf( '?' );
	if ( questionMarkIndex !== -1 ) {
		return url.slice( questionMarkIndex + 1 );
	}
	return '';
};
